import * as Y from 'yjs';
import { type Awareness } from 'y-protocols/awareness';
import { ActiveSelection, Canvas, FabricImage, FabricObject, util, ModifiedEvent } from 'fabric';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Fabric.js object with Yjs tracking ID
 * Extends FabricObject with the _yjsId property used for sync tracking
 */
export interface YjsTrackedFabricObject extends FabricObject {
    _yjsId?: string;
    /**
     * When true, the object is transient (e.g. a live awareness stroke/drag preview)
     * and is NEVER synced to Yjs: the canvas→Yjs handlers skip it on add/modify/remove.
     * Lets a consumer render ephemeral previews on the bound canvas without polluting
     * the object list. PT does not set this (default behavior unchanged).
     */
    _yjsEphemeral?: boolean;
}

/**
 * Serialized Fabric.js object data stored in Yjs.
 * This is the JSON representation created by FabricObject.toJSON()
 * extended with internal binding metadata (prefixed with _).
 */
export interface SerializedFabricObjectData {
    // Internal binding metadata (not Fabric.js properties)
    /** Internal: Unique ID for Yjs tracking and sync */
    _yjsId?: string;
    /** Internal: Layer for z-ordering (0=background, 1=foreground) */
    _layer?: number;

    // Fabric.js standard properties
    type: string;
    left?: number;
    top?: number;
    width?: number;
    height?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
    src?: string; // For FabricImage
    stateId?: string; // For TrafficLightObject phase state
    [key: string]: unknown; // Allow additional Fabric.js properties
}

/**
 * Minimal logger interface for FabricYjsBinding.
 * Duck-typed to avoid coupling this utility library to Angular DI or @pcp/logging.
 * Callers pass `inject(PCP_LOGGER).createScoped('yjs')` which satisfies this shape.
 */
export interface FabricYjsLogger {
    debug(message: string, properties?: Record<string, unknown>): void;
    warn(message: string, properties?: Record<string, unknown>): void;
    error(message: string, error?: Error, properties?: Record<string, unknown>): void;
}

/** No-op logger used when no logger is provided */
const NOOP_LOGGER: FabricYjsLogger = {
    debug: () => {},
    warn: () => {},
    error: () => {},
};

/**
 * Handler for a remote ephemeral (awareness) field update.
 *
 * The generic ephemeral relay lets any consumer stream high-frequency, transient
 * state through `y-protocols/awareness` (no Y.Doc clock advance / tombstones)
 * without the binding knowing the payload's domain meaning. PT registers a
 * `movingObject` field (vehicle/object drag); classroom registers a `drawingStroke`
 * field (in-progress freehand preview). Each consumer owns its own field + handler.
 *
 * @param payload The remote client's value for the field, or `null` when the field
 *   was cleared (sender ended the gesture) or the client departed — the handler
 *   should tear down any transient state it created for that client.
 * @param clientId The remote awareness client id; use it to key per-client transient
 *   objects (e.g. one preview stroke per remote drawer).
 * @returns `true` if the canvas needs a re-render after this update.
 */
export type EphemeralApplyHandler = (payload: unknown | null, clientId: number) => boolean | void;

/**
 * How a local background image is scaled into the canvas.
 *  - `'fill'` (default): independent X/Y scale to fill the canvas exactly — the original
 *    behavior; correct for fixed images whose aspect matches the logical canvas.
 *  - `'contain-height'`: uniform scale so the image HEIGHT matches the canvas; width may
 *    overflow — used for a horizontal panorama (the viewport transform pans across it).
 *  - `'contain-width'`: uniform scale so the image WIDTH matches the canvas; height may
 *    overflow — used for a vertical panorama.
 * The image is always anchored at the canvas world origin (top-left).
 */
export type BackgroundFit = 'fill' | 'contain-height' | 'contain-width';

/**
 * Scale factors that place an image of natural size `img` into a `canvas`-sized world under a
 * {@link BackgroundFit}, anchored at the world origin (top-left). This is the single source of
 * truth for both the canvas background ({@link FabricYjsBinding.setBackgroundLocal}) and any
 * full-frame overlay authored at the background's dimensions (the `GraphicOverlay` capability), so
 * the two can never drift: feeding the same fit + canvas size the same-sized image yields the same
 * scale, hence pixel-coincident placement.
 */
export function computeBackgroundScale(
    fit: BackgroundFit,
    img: { width: number; height: number },
    canvas: { width: number; height: number },
): { scaleX: number; scaleY: number } {
    switch (fit) {
        case 'contain-height':
            return { scaleX: canvas.height / img.height, scaleY: canvas.height / img.height };
        case 'contain-width':
            return { scaleX: canvas.width / img.width, scaleY: canvas.width / img.width };
        default:
            return { scaleX: canvas.width / img.width, scaleY: canvas.height / img.height };
    }
}

/** Awareness field name PT uses for object-drag position streaming (built-in handler). */
const MOVING_OBJECT_FIELD = 'movingObject';

/**
 * Callbacks for FabricYjsBinding to notify external listeners
 */
export interface FabricYjsBindingCallbacks {
    /**
     * Called whenever a background image is applied to the canvas.
     * This is triggered by both local changes (setBackground) and remote sync.
     * @param backgroundUrl - URL of the background image, or null if cleared
     */
    onBackgroundChanged?: (backgroundUrl: string | null) => void;

    /**
     * Called when an image object's src property changes from a remote client.
     * Used for starting/stopping turn signal blink animations when other clients toggle signals.
     * @param image - The FabricImage object whose src changed
     * @param newSrc - The new source URL
     */
    onImageSrcChangedFromRemote?: (image: FabricImage, newSrc: string) => void;

    /**
     * Called when an object's stateId property changes from a remote client.
     * Used for traffic light phase synchronization where stateId is the primary state identifier.
     * This is called BEFORE src changes are applied to ensure stateId takes precedence.
     * @param object - The FabricObject whose stateId changed
     * @param newStateId - The new state identifier
     */
    onStateIdChangedFromRemote?: (object: FabricObject, newStateId: string) => void;

    /**
     * Called when an object's turnSignalState property changes from a remote client.
     * Used for vehicle turn signal synchronization where turnSignalState is the primary state identifier.
     * This is called BEFORE other properties are applied to ensure animation starts immediately.
     * @param object - The FabricObject whose turnSignalState changed
     * @param newState - The new turn signal state ('off', 'left', 'right')
     */
    onTurnSignalStateChangedFromRemote?: (object: FabricObject, newState: string) => void;

    /**
     * Called after scene is loaded/synced from Yjs (initial load or remote state restore).
     * Used to restore animations (like turn signal blinking) for objects with active states.
     */
    onSceneLoaded?: () => void;

    /**
     * Called when an object is added to the canvas from a remote client (via Yjs sync).
     * Used to initialize object-specific behavior (e.g., start animation for defective traffic lights).
     * @param object - The FabricObject that was added
     */
    onObjectAddedFromRemote?: (object: FabricObject) => void;

    /**
     * Called before applying a remote src change to a FabricImage.
     * Return true to skip the src update (e.g., for locally animating objects).
     * @param image - The FabricImage about to receive src update
     * @param newSrc - The new source URL from remote
     * @returns true to skip the update, false to apply it
     */
    shouldSkipRemoteSrcUpdate?: (image: FabricImage, newSrc: string) => boolean;

    /**
     * Determine the z-order layer for an object based on its serialized data.
     * Used for automatic layer-based ordering where background objects (e.g., traffic lights,
     * shields) should always be below foreground objects (e.g., vehicles, drawings).
     *
     * @param objectData - The serialized object data
     * @returns The layer number (lower = behind, higher = in front), or undefined to use default (append)
     */
    getObjectLayer?: (objectData: SerializedFabricObjectData) => number | undefined;

    /**
     * Contribute extra properties to a LOCALLY created object, at the moment it is first
     * serialized and written to Y.Doc.
     *
     * The binding applies the returned properties to both the serialized data and the
     * fabric object itself (mirroring how `_yjsId` is written to both), so the object and
     * its Y state agree from the outset. Called once per local add, after the
     * `_yjsEphemeral` guard — transient previews are never stamped.
     *
     * Domain-neutral by design: the binding does not know or care what the properties
     * mean. The classroom uses it to record which slide *showing* a drawing belongs to;
     * a consumer that supplies nothing is unaffected.
     *
     * Keys the binding's own bookkeeping writes (`_yjsId`, `_layer`, `type`,
     * `_yjsEphemeral`) are ignored with a logged warning — see `RESERVED_STAMP_KEYS`.
     *
     * @param objectData - The serialized data of the object being added
     * @returns Properties to merge onto the object, or undefined to add none
     */
    stampLocalObject?: (objectData: SerializedFabricObjectData) => Record<string, unknown> | undefined;

    /**
     * Decide whether an object from Y state should be *visible* on this client.
     *
     * Consulted for objects arriving from Yjs — a full reconcile (`syncFromYjs`) and
     * remote inserts — **and again on every applied remote update**: an update re-applies
     * the full serialized state (which still carries `visible: true` from the original
     * serialization), so without the re-check a client that still renders the object
     * modifying it would resurrect it on every client that had hidden it. Absent callback
     * renders everything.
     *
     * **Returning false hides the object; it does NOT skip creating it.** That is
     * deliberate and load-bearing: the delete branch of `handleYArrayEvent` maps Y.Array
     * indices onto canvas objects *positionally* (through `_yjsId`-bearing objects — see
     * the comment there), so it requires one canvas object per Y.Array entry. Omitting an
     * object would shift that index space and make a later partial delete — a DEL keypress
     * or an undo — remove the wrong object. A rejected object is therefore added, then
     * hidden via `hideAsStale` (non-visible, non-interactive, non-exportable), keeping the
     * canvas a faithful positional projection of the array while showing nothing.
     *
     * A consumer must never un-hide a rejected object out-of-band — the next applied
     * update would only re-hide it. A predicate that ACCEPTS an object does not restore
     * visibility either; the object simply keeps whatever flags the update data carries.
     *
     * Note this cannot see objects that are on the canvas but absent from Y state (e.g.
     * added locally, or untracked after a rebind) — a consumer needing to enforce a rule
     * against *those* must also inspect the canvas itself.
     *
     * Domain-neutral by design: the classroom uses it to hide drawings stamped with a
     * superseded slide showing.
     *
     * @param objectData - The serialized object data from Y state
     * @returns true to show the object, false to add it hidden
     */
    shouldRender?: (objectData: SerializedFabricObjectData) => boolean;
}

/**
 * Fabric.js canvas event for object operations
 */
interface FabricObjectEvent {
    target: FabricObject;
}

/**
 * Y.Array delta change structure for Fabric objects
 * Used for type documentation - actual Yjs delta type is more permissive
 */
interface YArrayDeltaChange {
    retain?: number;
    delete?: number;
    insert?: Y.Map<unknown>[];
}

// ============================================================================
// Yjs Observer Type Aliases
// ============================================================================
// Note: These use 'any' due to Yjs library type constraints where YEvent<T>
// requires T extends AbstractType<any>. Using 'unknown' causes type errors.

type YjsDeepObserver = (events: Y.YEvent<any>[], transaction: Y.Transaction) => void;

type YjsMapObserver = (event: Y.YMapEvent<any>, transaction: Y.Transaction) => void;

// ============================================================================
// Hidden-stale state
// ============================================================================

/**
 * Put a fabric object into the shared "hidden stale" state: invisible, non-interactive
 * and excluded from export, while still occupying its canvas slot.
 *
 * This is the ONLY way an object rejected by a consumer rule (`shouldRender`, or a
 * consumer-side canvas sweep) may be suppressed — removing it instead would break the
 * one-canvas-object-per-Y.Array-entry positional mapping the delete path relies on.
 * Filter-hidden and sweep-hidden objects must stay indistinguishable, so every site
 * that hides must produce this exact flag set; centralizing the recipe here is what
 * enforces that.
 */
export function hideAsStale(obj: FabricObject): void {
    obj.visible = false;
    obj.evented = false;
    obj.selectable = false;
    obj.excludeFromExport = true;
}

/**
 * Keys the binding's own bookkeeping writes; a `stampLocalObject` result must not
 * overwrite them. Kept next to {@link hideAsStale} as the binding-owned contract
 * surface — extend this set whenever the binding starts writing a new key of its own.
 */
const RESERVED_STAMP_KEYS: ReadonlySet<string> = new Set(['_yjsId', '_layer', 'type', '_yjsEphemeral']);

// ============================================================================
// Payload Optimization Helpers
// ============================================================================

/**
 * Round path coordinates to reduce payload size.
 * Converts coordinates like 281.627408993576 to 281.63 (2 decimal places).
 * This is applied before syncing to ensure all clients receive identical coordinates.
 *
 * @param path - Fabric.js path array (e.g., [["M", 100.123456, 200.789012], ["Q", ...]])
 * @param precision - Number of decimal places (default: 2, gives 0.01 pixel precision)
 * @returns Path with rounded coordinates
 */
function roundPathCoordinates(path: unknown[][], precision: number = 2): unknown[][] {
    const factor = Math.pow(10, precision);
    return path.map(segment =>
        segment.map((val, i) =>
            // Index 0 is the command letter (M, Q, L, etc.), rest are coordinates
            i === 0 ? val : Math.round((val as number) * factor) / factor
        )
    );
}

/**
 * Optimize serialized Fabric object data for network transfer.
 * Currently rounds path coordinates to reduce payload size by ~30%.
 *
 * @param objectData - Serialized Fabric.js object
 * @returns Optimized object data
 */
function optimizeObjectData(objectData: SerializedFabricObjectData): SerializedFabricObjectData {
    // Round path coordinates for Path objects
    if (objectData['path'] && Array.isArray(objectData['path'])) {
        objectData['path'] = roundPathCoordinates(objectData['path'] as unknown[][]);
    }
    return objectData;
}

/**
 * FabricYjsBinding - Two-way binding between Fabric.js canvas and Y.Doc
 *
 * Synchronizes canvas objects with Y.Array<Y.Map> CRDT:
 * - CANVAS → YJS: Listens to Fabric events (object:added, object:modified, object:removed)
 * - YJS → CANVAS: Observes Y.Array changes and updates Fabric canvas
 * - LOOP PREVENTION: Uses `isApplyingRemote` flag to prevent infinite loops
 *
 * Also synchronizes background image via metadata Y.Map:
 * - Stores background image URL in metadata 'backgroundImage' key
 * - Automatically applies background on remote changes
 *
 * Architecture:
 * - Each canvas object is serialized to Y.Map (JSON representation)
 * - Y.Array maintains ordered list of objects
 * - Uses Fabric's `util.enlivenObjects()` for deserialization
 * - Uses `_yjsId` to track object identity across sync
 *
 * Usage:
 * ```typescript
 * const yDoc = new Y.Doc();
 * const objects = yDoc.getArray<Y.Map<unknown>>('objects');
 * const metadata = yDoc.getMap('metadata');
 * const binding = new FabricYjsBinding(canvas, objects, metadata);
 * binding.setBackground('http://example.com/bg.png'); // Syncs to all clients
 * binding.destroy(); // Cleanup when done
 * ```
 */
/**
 * Transaction origin marker for Fabric canvas → Yjs syncs.
 * Used to distinguish local canvas operations from UndoManager operations.
 * - Fabric → Yjs: origin = FABRIC_ORIGIN → skip in observer (avoid feedback loop)
 * - UndoManager → Yjs: origin = UndoManager instance → process in observer (apply undo/redo)
 * - Remote → Yjs: origin = provider instance → process in observer (apply remote changes)
 *
 * IMPORTANT: Y.UndoManager must be configured to track this origin:
 * `new Y.UndoManager(yArray, { trackedOrigins: new Set([FABRIC_ORIGIN]) })`
 */
export const FABRIC_ORIGIN = 'fabric-canvas-sync';

/**
 * Generate unique ID for tracking objects across Yjs sync.
 */
export function generateObjectId(): string {
    return `obj_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

export class FabricYjsBinding {
    private isApplyingRemote = false;

    // Fabric event handlers (stored for cleanup)
    private handleObjectAdded?: (e: FabricObjectEvent) => void;
    private handleObjectModified?: (e: ModifiedEvent) => void;
    private handleObjectRemoved?: (e: FabricObjectEvent) => void;

    // Yjs deep observer for objects array (stored for cleanup)
    private yObserver?: YjsDeepObserver;

    // Yjs metadata observer (stored for cleanup) - watches backgroundImage changes
    private yMetadataObserver?: YjsMapObserver;

    // Track objects being added asynchronously - maps _yjsId to Promise
    private pendingObjects = new Map<string, Promise<void>>();

    // Queue of Y.Map updates for objects that are still being added
    private pendingUpdates = new Map<string, SerializedFabricObjectData[]>();

    // Track actual Yjs instances with observers attached (for correct cleanup)
    // This prevents "[yjs] Tried to remove event handler that doesn't exist" errors
    // when Y.Array/Y.Map instances are replaced (e.g., Teacher closes/reopens PT)
    private observedObjectsArray?: Y.Array<Y.Map<unknown>>;
    private observedMetadata?: Y.Map<unknown>;

    // Awareness: ephemeral position sync during drag/steer (no Y.Doc tombstones)
    private awareness?: Awareness;
    // Opt-in (classroom): drop the local awareness state to `null` once no ephemeral
    // field is set, so y-protocols stops renewing it and the channel goes silent.
    // Off by default — PT reads `getStates()` for steering-lock/presence and must keep
    // its state alive. See {@link setAwareness}.
    private clearAwarenessWhenEmpty = false;
    private awarenessChangeHandler?: (changes: { added: number[]; updated: number[]; removed: number[] }) => void;

    // Generic ephemeral (awareness) relay: field name → apply handler. The single
    // awareness `change` listener dispatches each remote client's value for each
    // registered field to its handler. Domain-neutral: PT registers `movingObject`
    // (built-in, see setupAwarenessListener), classroom registers `drawingStroke`.
    private readonly ephemeralHandlers = new Map<string, EphemeralApplyHandler>();

    // O(1) object lookup cache for awareness listener (avoids O(n) canvas.getObjects().find())
    private yjsIdToObjectCache = new Map<string, YjsTrackedFabricObject>();

    // Optional hook fired at the end of a full `syncFromYjs` reconcile, AFTER all synced
    // objects are re-added but BEFORE `renderAll()`. Lets a consumer re-place its own
    // non-synced objects (e.g. classroom's content-band graphic overlays) below the
    // re-appended drawings within the same pass, so the first paint has the correct
    // z-order (flicker-free). Optional + null by default → PracticeTrainer is unaffected.
    private afterReconcileHandler?: () => void;

    // Local (non-synced) background applied via setBackgroundLocal. Remembered so a
    // re-sync (`syncFromYjs`, which clears the canvas) can restore it when the synced
    // `metadata.backgroundImage` is absent. Used by classroom (per-client variant
    // selection: the slide URL is synced elsewhere, each client resolves its own
    // low/med/high variant locally and never pushes image bytes through the binding).
    private localBackgroundUrl: string | null = null;

    // Fit mode for the local background (see {@link BackgroundFit}). Defaults to 'fill'
    // (original behavior) so PracticeTrainer and non-panorama classroom slides are
    // unaffected; classroom sets a 'contain-*' mode for panorama slides.
    private localBackgroundFit: BackgroundFit = 'fill';

    private readonly log: FabricYjsLogger;

    /**
     * @param canvas - Fabric.js canvas instance
     * @param objects - Y.Array containing canvas objects
     * @param metadata - Y.Map containing backgroundImage and other metadata
     * @param callbacks - Optional callbacks for external notifications (e.g., background changes)
     * @param logger - Optional logger instance. Pass `inject(PCP_LOGGER).createScoped('yjs')` from the calling service.
     */
    constructor(
        private readonly canvas: Canvas,
        private readonly objects: Y.Array<Y.Map<unknown>>,
        private readonly metadata: Y.Map<unknown>,
        private readonly callbacks?: FabricYjsBindingCallbacks,
        logger?: FabricYjsLogger
    ) {
        this.log = logger ?? NOOP_LOGGER;
        this.setupFabricListeners();
        this.setupMetadataObserver();
        this.setupYjsObserver();
        this.syncFromYjs();
    }

    /**
     * CANVAS → YJS: Listen to Fabric canvas events
     */
    private setupFabricListeners(): void {
        // Handle object added
        this.handleObjectAdded = (e: FabricObjectEvent) => {
            if (this.isApplyingRemote) {
                return; // Don't sync remote changes back to Yjs
            }

            const target = e.target as YjsTrackedFabricObject;
            if (!target) return;
            if (target._yjsEphemeral) return; // transient preview — never sync

            // Serialize object to JSON and optimize for network transfer
            const objectData: SerializedFabricObjectData = optimizeObjectData(
                target.toJSON() as SerializedFabricObjectData
            );

            // Store unique ID for tracking (on both canvas object and Yjs data)
            const objectId = this.generateObjectId();
            objectData._yjsId = objectId;
            target._yjsId = objectId;
            this.yjsIdToObjectCache.set(objectId, target);

            // Determine layer for z-ordering (if callback provided)
            let layer: number | undefined;
            try {
                layer = this.callbacks?.getObjectLayer?.(objectData);
            } catch (error) {
                this.log.warn('getObjectLayer callback threw', { error });
                layer = undefined;
            }
            if (layer !== undefined) {
                objectData._layer = layer;
            }

            // Consumer-contributed properties for locally created objects (e.g. the
            // classroom's slide-showing stamp). Applied to BOTH the serialized data and
            // the fabric object, like `_yjsId` above, so a consumer inspecting the canvas
            // sees the same values it will find in Y state. Never reached for ephemeral
            // previews — they returned early.
            try {
                const stamp = this.callbacks?.stampLocalObject?.(objectData);
                if (stamp) {
                    for (const [key, value] of Object.entries(stamp)) {
                        if (RESERVED_STAMP_KEYS.has(key)) {
                            // The id was already cached under the binding-assigned value and
                            // the insert index derives from the pre-stamp layer — letting a
                            // consumer overwrite these corrupts bookkeeping silently.
                            this.log.warn('stampLocalObject returned a reserved key — ignored', { key });
                            continue;
                        }
                        objectData[key] = value;
                        (target as unknown as Record<string, unknown>)[key] = value;
                    }
                }
            } catch (error) {
                this.log.warn('stampLocalObject callback threw', { error });
            }

            // Calculate insertion index based on layer (background before foreground)
            const insertIndex = layer !== undefined
                ? this.calculateInsertIndex(layer)
                : this.objects.length; // No layer = append to end

            // Batch Y.Map creation and all property setting into a single Yjs transaction
            // This ensures only ONE SignalR message is sent instead of one per property
            const didInsert = this.transact(() => {
                const yMap = new Y.Map();
                this.applyDataToYMap(yMap, objectData);
                // Insert at calculated position to maintain layer ordering
                this.objects.insert(insertIndex, [yMap]);
            });

            // Ensure canvas object is at the correct z-position
            // (canvas.add() appends to end, but we may have inserted earlier in Y.Array)
            if (didInsert) {
                this.ensureCanvasObjectAtIndex(target, insertIndex);
            }
        };

        // Handle object modified
        this.handleObjectModified = (e: ModifiedEvent) => {
            if (this.isApplyingRemote) {
                return;
            }

            const target = e.target;
            if (!target) return;
            if ((target as YjsTrackedFabricObject)._yjsEphemeral) return; // transient preview — never sync

            // Handle ActiveSelection (multiple objects selected and moved together)
            // When multiple objects are selected, Fabric fires object:modified with
            // ActiveSelection as target, but we need to sync each individual object
            // IMPORTANT: Objects in ActiveSelection have relative coordinates - we must
            // calculate absolute coordinates using the group's transform matrix
            // Note: Type check fallback added for testability (mocking instanceof is tricky)
            if (target instanceof ActiveSelection || target.type === 'activeselection') {
                const selection = target as ActiveSelection;
                const objects = selection.getObjects() as YjsTrackedFabricObject[];
                this.syncMultipleObjects(objects, selection);
                return;
            }

            // Single object modification
            this.syncMultipleObjects([target as YjsTrackedFabricObject]);
        };

        // Handle object removed
        this.handleObjectRemoved = (e: FabricObjectEvent) => {
            if (this.isApplyingRemote) {
                return;
            }

            const target = e.target;
            if (!target) return;
            if ((target as YjsTrackedFabricObject)._yjsEphemeral) return; // transient preview — never synced

            const index = this.findObjectIndexByTarget(target);

            if (index !== -1) {
                // IMPORTANT: Wrap in transaction with FABRIC_ORIGIN to prevent feedback loop.
                // Without this, the Y.Array delete event would be processed by our own observer,
                // causing a second object to be incorrectly removed from the canvas.
                this.transact(() => {
                    this.objects.delete(index, 1);
                });
            }
        };

        this.canvas.on('object:added', this.handleObjectAdded);
        this.canvas.on('object:modified', this.handleObjectModified);
        this.canvas.on('object:removed', this.handleObjectRemoved);
    }

    /**
     * Sync multiple objects' changes to Yjs (for ActiveSelection/multiselect)
     * Batches all updates into a single Yjs transaction for efficiency
     *
     * @param objects - The objects to sync
     * @param activeSelection - Optional ActiveSelection containing the objects.
     *                          When provided, calculates absolute coordinates from relative group coords.
     */
    private syncMultipleObjects(objects: YjsTrackedFabricObject[], activeSelection?: ActiveSelection): void {

        // Get the group's transform matrix if we have an ActiveSelection
        // This is needed to convert relative object coordinates to absolute canvas coordinates
        const groupMatrix = activeSelection?.calcTransformMatrix();

        // Batch ALL object updates into a single Yjs transaction
        // This ensures only ONE SignalR message is sent for the entire multiselect move
        this.transact(() => {
            for (const obj of objects) {
                const index = this.findObjectIndexByTarget(obj);
                if (index === -1) continue;

                const yMap = this.objects.get(index);
                if (!yMap) continue;

                // Update Y.Map with new object data (optimized for network transfer)
                let objectData: SerializedFabricObjectData = optimizeObjectData(
                    obj.toJSON() as SerializedFabricObjectData
                );

                // If object is in an ActiveSelection, calculate absolute coordinates
                // Objects in a selection have coordinates relative to the group center
                if (groupMatrix) {
                    const objectMatrix = obj.calcOwnMatrix();
                    const absoluteMatrix = util.multiplyTransformMatrices(groupMatrix, objectMatrix);
                    const decomposed = util.qrDecompose(absoluteMatrix);

                    // IMPORTANT: translateX/translateY from qrDecompose are CENTER coordinates.
                    // Fabric's left/top depend on originX/originY. To avoid offset issues,
                    // we force center origin so left/top correctly represent the center point.
                    objectData = {
                        ...objectData,
                        left: decomposed.translateX,
                        top: decomposed.translateY,
                        scaleX: decomposed.scaleX,
                        scaleY: decomposed.scaleY,
                        angle: decomposed.angle,
                        originX: 'center',
                        originY: 'center',
                    };
                }

                // Preserve the _yjsId
                objectData._yjsId = obj._yjsId;

                this.applyDataToYMap(yMap, objectData);
            }
        });
    }

    /**
     * Setup observer for metadata changes (backgroundImage)
     */
    private setupMetadataObserver(): void {
        this.yMetadataObserver = (event: Y.YMapEvent<any>, transaction: Y.Transaction) => {
            // Skip if this change originated from our own Fabric canvas operations
            if (transaction.origin === FABRIC_ORIGIN) return;

            // Handle backgroundImage changes
            if (event.keysChanged.has('backgroundImage')) {
                const bgUrl = this.metadata.get('backgroundImage') as string | null;
                this.isApplyingRemote = true;
                this.applyBackgroundImage(bgUrl).finally(() => {
                    this.isApplyingRemote = false;
                });
            }
        };

        // Track the metadata instance for correct cleanup
        this.observedMetadata = this.metadata;
        this.metadata.observe(this.yMetadataObserver);
    }

    /**
     * YJS → CANVAS: Observe Y.Array and nested Y.Map changes
     * Uses observeDeep to catch:
     * - Y.Array changes (insert/delete) for add/remove objects
     * - Y.Map changes (set) for object modifications (move, resize, etc.)
     */
    private setupYjsObserver(): void {
        // Store reference for cleanup - ensures we unobserve from the correct instance
        this.observedObjectsArray = this.objects;

        this.yObserver = (events: Y.YEvent<any>[], transaction: Y.Transaction) => {
            // Skip if this change originated from our own Fabric canvas operations
            // FABRIC_ORIGIN = local Fabric → Yjs changes (avoid feedback loop)
            // Other origins (provider, UndoManager, etc.) should be processed
            if (transaction.origin === FABRIC_ORIGIN) {
                return;
            }

            this.isApplyingRemote = true;

            try {
                // IMPORTANT: Discard active selection before applying remote updates.
                // When objects are in an ActiveSelection, their coordinates are relative to
                // the group center. Applying absolute coordinates from yMap while objects
                // are still in the selection causes incorrect positioning.
                // Discarding the selection ensures all objects have absolute coordinates.
                const activeSelection = this.canvas.getActiveObject();
                if (activeSelection && activeSelection.type === 'activeselection') {
                    this.canvas.discardActiveObject();
                }

                events.forEach(event => {
                    if (event instanceof Y.YArrayEvent) {
                        this.handleYArrayEvent(event);
                    } else if (event instanceof Y.YMapEvent) {
                        this.handleYMapEvent(event);
                    }
                });

                this.canvas.renderAll();
            } finally {
                this.isApplyingRemote = false;
            }
        };

        this.objects.observeDeep(this.yObserver);
    }

    /**
     * Detach Yjs observer from the stored Y.Array instance
     * Uses observedObjectsArray to ensure we unobserve
     * from the exact instance the observer was attached to.
     */
    private detachYjsObserver(): void {
        if (this.yObserver && this.observedObjectsArray) {
            this.observedObjectsArray.unobserveDeep(this.yObserver);
            this.observedObjectsArray = undefined;
        }
    }

    /**
     * Apply background image to canvas (internal helper)
     * Notifies listeners via onBackgroundChanged callback after applying.
     */
    private async applyBackgroundImage(imgSrc: string | null): Promise<void> {
        if (!imgSrc) {
            // Clear background
            this.canvas.backgroundImage = undefined;
            this.canvas.renderAll();
            // Notify listener about background cleared
            this.callbacks?.onBackgroundChanged?.(null);
            return;
        }

        try {
            const img = await FabricImage.fromURL(imgSrc, { crossOrigin: 'anonymous' });
            if (img) {
                // Scale per fit mode and anchor at the world origin (top-left). 'fill'
                // distorts to fill exactly (original behavior); 'contain-*' scales
                // uniformly so one axis matches the canvas and the other overflows for
                // the viewport-transform to pan across (panorama). Shared with GraphicOverlay
                // via computeBackgroundScale so background + overlay can never drift.
                const { scaleX, scaleY } = computeBackgroundScale(
                    this.localBackgroundFit,
                    { width: img.width!, height: img.height! },
                    { width: this.canvas.getWidth(), height: this.canvas.getHeight() },
                );
                img.set({ originX: 'left', originY: 'top', left: 0, top: 0, scaleX, scaleY });
            }
            this.canvas.backgroundImage = img;
            this.canvas.renderAll();
            // Notify listener about background change
            this.callbacks?.onBackgroundChanged?.(imgSrc);
        } catch (error) {
            this.log.error('Failed to apply background image', error as Error);
        }
    }

    /**
     * Set canvas background image (syncs to all clients via Yjs)
     * @param imgSrc - URL of the background image, or null to clear
     */
    setBackground(imgSrc: string | null): void {
        // Update Yjs metadata — wrap in FABRIC_ORIGIN transaction so our own
        // metadata observer skips it (otherwise applyBackgroundImage fires twice)
        this.transact(() => {
            this.metadata.set('backgroundImage', imgSrc);
        });

        // Apply locally (don't wait for Yjs observer since it skips FABRIC_ORIGIN)
        this.applyBackgroundImage(imgSrc);
    }

    /**
     * Clear canvas background image (syncs to all clients via Yjs)
     */
    clearBackground(): void {
        this.setBackground(null);
    }

    /**
     * Set the canvas background LOCALLY — applied to this client only, never written
     * to the synced `metadata` Y.Map. Use when the background reference is already
     * synced through another channel and each client resolves its own variant (the
     * classroom case: the slide image URL lives in lesson state, and each client picks
     * its own low/med/high resolution by URL convention — no image bytes cross the wire,
     * and clients with different variants stay geometrically aligned in logical space).
     *
     * Contrast with {@link setBackground}, which syncs one URL to every client.
     * The value is remembered so a later {@link syncFromYjs} (which clears the canvas)
     * re-applies it when the synced metadata background is absent.
     *
     * @param imgSrc - URL of the background image, or null to clear.
     * @param fit - How to scale the image into the canvas (default `'fill'`, the original
     *   behavior). Use `'contain-height'` / `'contain-width'` for panorama backgrounds.
     */
    setBackgroundLocal(imgSrc: string | null, fit: BackgroundFit = 'fill'): void {
        this.localBackgroundUrl = imgSrc;
        this.localBackgroundFit = fit;
        this.applyBackgroundImage(imgSrc);
    }

    /**
     * Register (or clear, with `undefined`) a hook fired at the end of every full
     * {@link syncFromYjs} reconcile — after all synced objects are re-added, before the
     * render. A consumer uses it to re-place its own non-synced objects (which
     * `canvas.clear()` removed) into the correct z-band below the re-appended drawings,
     * so the first paint after a reconcile has the right stacking order (flicker-free).
     * Additive + off by default, so consumers that don't set it (e.g. PracticeTrainer)
     * are unaffected.
     */
    setAfterReconcile(handler: (() => void) | undefined): void {
        this.afterReconcileHandler = handler;
    }

    /**
     * Clear all objects from the canvas and Yjs (syncs to all clients via Yjs).
     * Use this instead of canvas.clear() to ensure proper sync to remote clients.
     *
     * IMPORTANT: Fabric.js canvas.clear() does NOT fire individual object:removed events,
     * so Yjs never gets notified of the deletions. This method directly clears the Y.Array,
     * which properly syncs the deletion to all clients.
     */
    clearAllObjects(): void {
        // Clear Y.Array first (syncs to all clients)
        this.transact(() => {
            if (this.objects.length > 0) {
                this.objects.delete(0, this.objects.length);
            }
        });

        // Clear local canvas - handleObjectRemoved won't fire events because
        // Y.Array is already empty, so no sync loop issues
        this.isApplyingRemote = true;
        this.canvas.clear();
        this.yjsIdToObjectCache.clear();
        this.isApplyingRemote = false;
    }

    /**
     * Full object sync to Yjs (persisted to Redis).
     * Use this after programmatic position changes that bypass Fabric's object:modified event
     * (e.g., after follow drag ends) to ensure the final state is persisted.
     *
     * @param target - The Fabric object to fully sync
     */
    syncObjectFull(target: YjsTrackedFabricObject): void {
        if (this.isApplyingRemote) return;
        this.syncMultipleObjects([target]);
    }

    /**
     * Sync only style properties (stroke, strokeWidth, strokeDashArray) for style changes.
     * More efficient and safer than full object sync when changing styles on selected objects.
     *
     * Unlike full sync via object:modified, this:
     * - Only updates style properties, not position/transform
     * - Avoids issues with selection state affecting serialization
     *
     * @param target - The Fabric object to sync styles for
     */
    syncStylesOnly(target: YjsTrackedFabricObject): void {
        this.syncMultipleStylesOnly([target]);
    }

    /**
     * Sync style properties for multiple objects in a single Yjs transaction.
     * This ensures all style changes are grouped for undo/redo.
     *
     * Performance: O(n) complexity - builds _yjsId → index map once before transaction,
     * avoiding O(n²) from repeated findObjectIndexByTarget() calls.
     *
     * @param targets - Array of Fabric objects to sync styles for
     */
    syncMultipleStylesOnly(targets: YjsTrackedFabricObject[]): void {
        if (this.isApplyingRemote) return;
        if (targets.length === 0) return;

        // Build _yjsId → index map once (O(n) instead of O(n²) from repeated searches)
        const indexMap = new Map<string, number>();
        for (let i = 0; i < this.objects.length; i++) {
            const yMap = this.objects.get(i);
            const yjsId = yMap?.get('_yjsId') as string | undefined;
            if (yjsId) {
                indexMap.set(yjsId, i);
            }
        }

        // Batch all style updates into a single Yjs transaction
        // This ensures undo/redo treats all changes as one operation
        this.transact(() => {
            for (const target of targets) {
                const targetId = target._yjsId;
                if (!targetId) continue;

                const index = indexMap.get(targetId);
                if (index === undefined) continue;

                const yMap = this.objects.get(index);
                if (!yMap) continue;

                // Only sync style properties - preserves position data as-is
                yMap.set('stroke', target.stroke);
                yMap.set('strokeWidth', target.strokeWidth);
                yMap.set('strokeDashArray', target.strokeDashArray);
            }
        });
    }

    /**
     * Handle Y.Array insert/delete events (add/remove canvas objects)
     */
    private handleYArrayEvent(event: Y.YArrayEvent<Y.Map<unknown>>): void {
        let retain = 0;
        // Yjs delta type is dynamic; cast to our expected structure for Fabric objects
        (event.changes.delta as YArrayDeltaChange[]).forEach((change) => {
            if (change.retain) {
                retain += change.retain;
            } else if (change.delete) {
                // Map Y.Array indices to canvas objects through binding-tracked (synced) objects
                // ONLY. The canvas may also hold non-synced capability objects (e.g. GraphicOverlay
                // images flagged `_yjsEphemeral`) that are absent from the Y.Array, so a raw canvas
                // index is NOT the Y.Array index once such objects are present — indexing the raw
                // `getObjects()` would remove the wrong objects (or a graphic overlay) and leave the
                // intended drawings behind. Filtering to `_yjsId`-bearing objects keeps the index
                // space aligned with the Y.Array. No-op for all-synced canvases (e.g. PracticeTrainer),
                // where every canvas object carries a `_yjsId`.
                const syncedObjects = this.canvas.getObjects().filter(
                    (o): o is YjsTrackedFabricObject => !!(o as YjsTrackedFabricObject)._yjsId,
                );
                const objectsToRemove: FabricObject[] = [];
                for (let i = 0; i < change.delete; i++) {
                    const obj = syncedObjects[retain + i];
                    if (!obj) {
                        this.log.warn('Canvas/Y.Array out of sync: no synced canvas object at index', {
                            index: retain + i,
                            syncedLength: syncedObjects.length,
                            deleteCount: change.delete,
                        });
                        continue;
                    }
                    const removedId = obj._yjsId;
                    if (removedId) {
                        this.yjsIdToObjectCache.delete(removedId);
                        this.pendingObjects.delete(removedId);
                        this.pendingUpdates.delete(removedId);
                    }
                    objectsToRemove.push(obj);
                }
                for (const obj of objectsToRemove) {
                    this.canvas.remove(obj);
                }
            } else if (change.insert) {
                // Add objects to canvas at the correct z-index position
                change.insert.forEach((yMap: Y.Map<unknown>, insertOffset: number) => {
                    const objectData = this.yMapToObject(yMap);
                    const yjsId = objectData._yjsId;
                    // Calculate the target canvas index: retain position + offset within insert batch
                    const insertAtIndex = retain + insertOffset;

                    // Track this object as pending and store the promise
                    if (yjsId) {
                        const addPromise = this.addObjectToCanvas(objectData, insertAtIndex).then(() => {
                            // Only clean up if this is still the active promise for this object.
                            // A delete+re-add (undo/redo) may have replaced the entry.
                            if (this.pendingObjects.get(yjsId) === addPromise) {
                                this.pendingObjects.delete(yjsId);
                                this.processPendingUpdates(yjsId);
                            }
                        });
                        this.pendingObjects.set(yjsId, addPromise);
                    } else {
                        this.addObjectToCanvas(objectData, insertAtIndex);
                    }
                });
                // Update retain to account for inserted objects (for subsequent delta operations)
                retain += change.insert.length;
            }
        });
    }

    /**
     * Handle Y.Map property change events (object modifications like move, resize)
     */
    private handleYMapEvent(event: Y.YMapEvent<unknown>): void {
        const yMap = event.target as Y.Map<unknown>;
        const objectId = yMap.get('_yjsId') as string | undefined;

        if (!objectId) {
            return;
        }

        // Find the corresponding canvas object
        const canvasObj = this.findCanvasObjectById(objectId);

        // Check if object is still being added asynchronously OR not yet on canvas
        if (this.pendingObjects.has(objectId) || !canvasObj) {
            // Queue this update to be applied after object is added
            // For pending objects, we need full state since object doesn't exist yet
            const newData = this.yMapToObject(yMap);
            if (!this.pendingUpdates.has(objectId)) {
                this.pendingUpdates.set(objectId, []);
            }
            this.pendingUpdates.get(objectId)!.push(newData);
            return;
        }

        // IMPORTANT: Always use applyUpdateToCanvasObject for receiving updates.
        // This ensures:
        // 1. Async operations (image src loading) work correctly
        // 2. Undo/redo operations work correctly
        // 3. Multi-selection coordinates are handled properly
        //
        // The syncStylesOnly methods optimize what we SEND, but the receiving
        // side should always apply full updates from yMap (which contains current truth).
        this.applyUpdateToCanvasObject(canvasObj, yMap);
    }

    /**
     * Apply Y.Map data to a canvas object
     */
    private async applyUpdateToCanvasObject(
        canvasObj: FabricObject,
        yMapOrData: Y.Map<unknown> | SerializedFabricObjectData
    ): Promise<void> {
        // Get data either from Y.Map or plain object
        const newData: SerializedFabricObjectData = yMapOrData instanceof Y.Map
            ? this.yMapToObject(yMapOrData)
            : { ...yMapOrData };

        // Handle state property changes BEFORE src change - state takes precedence for sync.
        // Properties are still applied via set() below for consistency.
        this.notifyRemotePropertyChange(canvasObj, newData, 'stateId', this.callbacks?.onStateIdChangedFromRemote);
        this.notifyRemotePropertyChange(canvasObj, newData, 'turnSignalState', this.callbacks?.onTurnSignalStateChangedFromRemote);

        // Handle FabricImage src change specially - set() doesn't reload the image
        // Use instanceof to handle both FabricImage and subclasses (e.g., VehicleObject)
        if (canvasObj instanceof FabricImage && newData.src) {
            const fabricImage = canvasObj;
            const currentSrc = fabricImage.getSrc?.() || '';
            if (newData.src !== currentSrc) {
                // Check if we should skip this src update (e.g., for locally animating objects)
                const shouldSkip = this.callbacks?.shouldSkipRemoteSrcUpdate?.(fabricImage, newData.src);
                if (!shouldSkip) {
                    // Use setSrc() to actually load the new image
                    await fabricImage.setSrc(newData.src);
                    this.canvas.renderAll(); // Re-render after image loads
                    // Notify listener about src change (used for turn signal blink animation)
                    this.callbacks?.onImageSrcChangedFromRemote?.(fabricImage, newData.src);
                }
                delete newData.src; // Remove so we don't set it again below (even if skipped)
            }
        }

        // Strip metadata (_yjsId) and read-only props (type) before applying
        const { _yjsId: _, type: __, ...propsToApply } = newData;
        canvasObj.set(propsToApply);
        canvasObj.setCoords();

        // Suppression must survive updates: the Y state still carries `visible: true` from
        // the object's original serialization, so the set() above just re-applied it.
        // Re-consult the consumer verdict and re-hide — otherwise a client that still
        // renders this object (its own state matches the object's stamp) modifying it
        // would resurrect it on every client that had hidden it. processPendingUpdates
        // funnels through here too, covering an update that raced the async add.
        if (!this.consumerAllowsRender(newData)) {
            hideAsStale(canvasObj);
        }
    }

    /**
     * Consumer verdict on whether an object from Y state may be visible. Fails open —
     * a throwing predicate must never hide content. Shared by the add path and the
     * update path so the two cannot drift.
     */
    private consumerAllowsRender(objectData: SerializedFabricObjectData): boolean {
        try {
            return this.callbacks?.shouldRender?.(objectData) ?? true;
        } catch (error) {
            this.log.warn('shouldRender callback threw', { error });
            return true;
        }
    }

    /**
     * Process queued updates for an object that just finished being added
     */
    private processPendingUpdates(objectId: string): void {
        const updates = this.pendingUpdates.get(objectId);
        if (!updates || updates.length === 0) {
            this.pendingUpdates.delete(objectId);
            return;
        }

        const canvasObj = this.findCanvasObjectById(objectId);
        if (canvasObj) {
            // Apply only the last update (most recent state)
            const lastUpdate = updates[updates.length - 1];
            this.applyUpdateToCanvasObject(canvasObj, lastUpdate);
            this.canvas.renderAll();
        }

        this.pendingUpdates.delete(objectId);
    }

    /**
     * Initial sync: Load all objects and metadata from Yjs to canvas
     * Async to properly await object loading before notifying listeners
     *
     * Async to properly await object loading before notifying listeners.
     */
    private async syncFromYjs(): Promise<void> {
        // Check if there's anything to sync. Prefer the synced metadata background;
        // fall back to a locally-set background (setBackgroundLocal) so it survives the
        // canvas.clear() below — the local background is never stored in metadata.
        const metaBg = this.metadata.get('backgroundImage') as string | null | undefined;
        const bgUrl = metaBg ?? this.localBackgroundUrl ?? undefined;
        const hasObjectsToSync = this.objects.length > 0;
        const hasBackgroundToSync = !!bgUrl;

        // If nothing to sync, skip entirely
        // This prevents isApplyingRemote from blocking canvas operations during initialization
        if (!hasObjectsToSync && !hasBackgroundToSync) {
            return;
        }

        this.isApplyingRemote = true;

        try {
            // Preserve background color before clearing (canvas.clear() removes it)
            const bgColor = this.canvas.backgroundColor;

            this.canvas.clear();
            this.yjsIdToObjectCache.clear();

            // Restore background color (e.g., 'white' default when no map is selected)
            this.canvas.backgroundColor = bgColor;

            // Sync background image FIRST (so it's under objects)
            if (bgUrl) {
                await this.applyBackgroundImage(bgUrl);
            }

            // Sync objects - collect all promises
            const addPromises: Promise<void>[] = [];
            this.objects.forEach((yMap) => {
                const objectData = this.yMapToObject(yMap);
                if (!objectData.type) {
                    this.log.error('Skipping object with missing type — possible data corruption', undefined,
                        { _yjsId: objectData._yjsId, keys: Object.keys(objectData) });
                    return;
                }
                addPromises.push(this.addObjectToCanvas(objectData));
            });

            // Wait for ALL objects to be added to canvas before proceeding
            await Promise.all(addPromises);

            // Let a consumer re-place its non-synced objects (below the just-re-added
            // drawings) BEFORE the first render — flicker-free content-band ordering.
            this.afterReconcileHandler?.();

            this.canvas.renderAll();

            // Notify listener that scene is loaded (used to restore animations like turn signal blinking)
            // Now safe to call because all objects are guaranteed to be on canvas
            this.callbacks?.onSceneLoaded?.();
        } finally {
            this.isApplyingRemote = false;
        }
    }

    /**
     * Convert Y.Map to SerializedFabricObjectData
     */
    private yMapToObject(yMap: Y.Map<unknown>): SerializedFabricObjectData {
        const obj: Record<string, unknown> = {};
        yMap.forEach((value, key) => {
            obj[key] = value;
        });
        return obj as SerializedFabricObjectData;
    }

    /**
     * Add object to canvas from JSON data
     * @param objectData - Serialized object data to recreate
     * @param insertAtIndex - Optional z-index position to insert at (for layer ordering)
     */
    private async addObjectToCanvas(
        objectData: SerializedFabricObjectData,
        insertAtIndex?: number
    ): Promise<void> {
        const yjsId = objectData._yjsId;

        try {
            // Use Fabric.js v6 util.enlivenObjects to recreate object from JSON
            const objects = await util.enlivenObjects<FabricObject>([objectData]);

            if (objects.length === 0) {
                this.log.warn('enlivenObjects returned empty array', { yjsId });
                // Clean up pending state for failed object
                if (yjsId) {
                    this.pendingUpdates.delete(yjsId);
                }
                return;
            }

            // Guard against ghost objects: if this add was tracked in pendingObjects
            // (i.e., came from handleYArrayEvent) but the entry was removed by the
            // delete branch, the object was deleted while we were awaiting enlivenObjects.
            // syncFromYjs does NOT populate pendingObjects, so we only check when an
            // entry was expected (insertAtIndex !== undefined signals handleYArrayEvent).
            if (yjsId && insertAtIndex !== undefined && !this.pendingObjects.has(yjsId)) {
                this.log.debug('Object deleted while pending, skipping add', { yjsId });
                return;
            }

            const obj = objects[0] as YjsTrackedFabricObject;

            // Preserve _yjsId from remote object data for tracking
            if (yjsId) {
                obj._yjsId = yjsId;
                this.yjsIdToObjectCache.set(yjsId, obj);
            }

            // Consumer veto on VISIBILITY (not on creation). The object is still added, so
            // the Y.Array-index → canvas-object mapping in handleYArrayEvent's delete branch
            // stays one-to-one; skipping the add would shift that index space and make a
            // later partial delete (DEL key, undo) remove the wrong object. See the
            // `shouldRender` docs.
            if (!this.consumerAllowsRender(objectData)) {
                hideAsStale(obj);
            }

            // IMPORTANT: Set isApplyingRemote before canvas.add() to prevent
            // handleObjectAdded from generating a new _yjsId and syncing back.
            // This is needed because util.enlivenObjects is async, and by the time
            // it resolves, the isApplyingRemote flag from yObserver has been reset.
            const wasApplyingRemote = this.isApplyingRemote;
            this.isApplyingRemote = true;
            try {
                this.canvas.add(obj);

                // If insertAtIndex specified, move to correct z-position (for layer ordering)
                if (insertAtIndex !== undefined) {
                    this.ensureCanvasObjectAtIndex(obj, insertAtIndex);
                }

                // Notify listener about object added from remote (e.g., for starting animations)
                this.callbacks?.onObjectAddedFromRemote?.(obj);
            } finally {
                this.isApplyingRemote = wasApplyingRemote;
            }
        } catch (error) {
            this.log.error('Failed to add object to canvas', error as Error);
            // Clean up pending state on failure
            if (yjsId) {
                this.pendingObjects.delete(yjsId);
                this.pendingUpdates.delete(yjsId);
            }
        }
    }

    /**
     * Find Y.Array index by matching canvas object's _yjsId
     */
    private findObjectIndexByTarget(target: FabricObject): number {
        const targetId = (target as YjsTrackedFabricObject)._yjsId;
        if (!targetId) return -1;

        for (let i = 0; i < this.objects.length; i++) {
            const yMap = this.objects.get(i);
            if (yMap?.get('_yjsId') === targetId) {
                return i;
            }
        }
        return -1;
    }

    /**
     * Find canvas object by _yjsId
     */
    private findCanvasObjectById(yjsId: string): YjsTrackedFabricObject | undefined {
        const cached = this.yjsIdToObjectCache.get(yjsId);
        if (cached) return cached;

        // Fallback: linear scan + cache-on-miss (defensive, shouldn't normally be needed)
        const found = this.canvas.getObjects().find(
            (obj): obj is YjsTrackedFabricObject => (obj as YjsTrackedFabricObject)._yjsId === yjsId
        );
        if (found) this.yjsIdToObjectCache.set(yjsId, found);
        return found;
    }

    /**
     * Execute a function inside a Yjs transaction with null-safe doc access
     */
    private transact(fn: () => void, origin: string = FABRIC_ORIGIN): boolean {
        const doc = this.objects.doc;
        if (!doc) return false;
        doc.transact(fn, origin);
        return true;
    }

    /**
     * Notify callback if a property changed on a remote update.
     * Used for state properties (stateId, turnSignalState) that need special handling.
     */
    private notifyRemotePropertyChange(
        canvasObj: FabricObject,
        newData: SerializedFabricObjectData,
        prop: string,
        callback?: (obj: FabricObject, value: string) => void
    ): void {
        const newValue = newData[prop] as string | undefined;
        if (newValue === undefined) return;
        const currentValue = (canvasObj as unknown as Record<string, unknown>)[prop] as string | undefined;
        if (newValue !== currentValue) {
            callback?.(canvasObj, newValue);
        }
    }

    /**
     * Apply all properties from serialized data to a Y.Map
     */
    private applyDataToYMap(yMap: Y.Map<unknown>, data: SerializedFabricObjectData): void {
        for (const key of Object.keys(data)) {
            yMap.set(key, data[key as keyof SerializedFabricObjectData]);
        }
    }

    /**
     * Generate unique ID for tracking objects
     */
    private generateObjectId(): string {
        return generateObjectId();
    }

    /**
     * Calculate the insertion index for a new object based on its layer.
     * Background objects (lower layer numbers) are inserted before foreground objects.
     *
     * Only called for local user adds (handleObjectAdded). Bulk paths (syncFromYjs,
     * handleYArrayEvent) set isApplyingRemote=true, causing handleObjectAdded to
     * return early. The O(n) scan is acceptable for interactive single-object adds.
     *
     * @param layer - The layer number for the new object
     * @returns The Y.Array index where the object should be inserted
     */
    private calculateInsertIndex(layer: number): number {
        if (this.objects.length === 0) return 0;

        // Find the first object with a higher layer number
        for (let i = 0; i < this.objects.length; i++) {
            const objLayer = this.objects.get(i)?.get('_layer') as number | undefined;
            // Treat undefined layer as maximum (foreground) for backward compatibility
            const effectiveLayer = objLayer ?? Number.MAX_SAFE_INTEGER;
            if (effectiveLayer > layer) {
                return i;
            }
        }
        // All existing objects have the same or lower layer, insert at end
        return this.objects.length;
    }

    /**
     * Ensure a canvas object is at the correct z-index position.
     * Called after adding/inserting to sync canvas z-order with Y.Array order.
     *
     * @param obj - The Fabric.js object to reposition
     * @param targetIndex - The target z-index position
     */
    private ensureCanvasObjectAtIndex(obj: FabricObject, targetIndex: number): void {
        const objects = this.canvas.getObjects();
        const currentIndex = objects.indexOf(obj);

        if (currentIndex === -1 || currentIndex === targetIndex) return;

        // Use Fabric's moveObjectTo to reposition in the z-order
        this.canvas.moveObjectTo(obj, targetIndex);
    }

    // ============================================================================
    // Awareness: Ephemeral Position Sync (no Y.Doc tombstones)
    // ============================================================================

    /**
     * Set the Awareness instance for ephemeral position sync.
     * Call after provider connects. In local-only mode, don't call this (awareness stays undefined).
     *
     * Registers the built-in `movingObject` handler (PT object-drag) so the existing
     * `syncPositionViaAwareness`/`clearMovingObjectAwareness` API keeps working with no
     * consumer change. Additional fields (e.g. classroom `drawingStroke`) are added via
     * {@link registerEphemeralField}.
     */
    setAwareness(awareness: Awareness, opts?: { clearWhenEmpty?: boolean }): void {
        this.awareness = awareness;
        this.clearAwarenessWhenEmpty = opts?.clearWhenEmpty ?? false;
        this.registerEphemeralField(MOVING_OBJECT_FIELD, (payload) => this.applyMovingObject(payload));
        this.setupAwarenessListener();
    }

    // ------------------------------------------------------------------------
    // Generic ephemeral (awareness) relay
    // ------------------------------------------------------------------------

    /**
     * Register a handler for a remote ephemeral (awareness) field. The single
     * awareness listener dispatches each remote client's value for `field` to
     * `apply` (and `apply(null, clientId)` when that client clears the field or
     * departs, so the handler can tear down per-client transient state).
     *
     * Domain-neutral: the binding never interprets the payload. Used to share the
     * awareness transport between PT (`movingObject`) and classroom (`drawingStroke`)
     * without either domain leaking into the core.
     */
    registerEphemeralField(field: string, apply: EphemeralApplyHandler): void {
        this.ephemeralHandlers.set(field, apply);
    }

    /** Remove a previously-registered ephemeral field handler. */
    unregisterEphemeralField(field: string): void {
        this.ephemeralHandlers.delete(field);
    }

    /**
     * Publish a local ephemeral value for `field` over awareness (no Y.Doc write).
     * Pass `null` to clear the field (signals remote handlers to tear down).
     * No-op when awareness is not attached (local-only mode).
     *
     * With `clearWhenEmpty` (classroom), a cleared field is **deleted** rather than set to
     * `null`, and the whole local state drops to `null` once nothing is left — that is what
     * stops y-protocols renewing it every 15s. Receivers are unaffected: the listener reads
     * `state?.[field] ?? null`, so a deleted key and a `null` value are indistinguishable.
     *
     * Two traps this avoids:
     * - `setLocalStateField` is a **no-op when the local state is `null`**, so a publish from
     *   idle must go through `setLocalState`, or the field would silently never appear.
     * - The classroom's three layer bindings share ONE awareness instance. The remaining-keys
     *   check therefore reads the live shared state on every call: clearing `slideViewBox:1`
     *   must not wipe an active `slideViewBox:2` published by another layer's binding.
     */
    publishEphemeral(field: string, payload: unknown | null): void {
        if (!this.awareness) {
            return;
        }

        if (!this.clearAwarenessWhenEmpty) {
            this.awareness.setLocalStateField(field, payload ?? null);
            return;
        }

        const current = this.awareness.getLocalState();

        if (payload != null) {
            this.awareness.setLocalState({ ...(current ?? {}), [field]: payload });
            return;
        }

        if (!current || !(field in current)) {
            return; // already clear — don't resurrect the state object
        }

        const remaining: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(current)) {
            if (key !== field) {
                remaining[key] = value;
            }
        }
        this.awareness.setLocalState(Object.keys(remaining).length > 0 ? remaining : null);
    }


    /**
     * Sync object position via Awareness (ephemeral, no Y.Doc writes).
     * Use for continuous movement updates (drag, physics steering).
     * Called per frame (~60/sec) — physics engine is the natural rate limiter.
     *
     * The final position must be persisted via syncObjectFull() when movement ends.
     */
    syncPositionViaAwareness(target: YjsTrackedFabricObject): void {
        const objectId = target._yjsId;
        if (!objectId) return;

        this.publishEphemeral(MOVING_OBJECT_FIELD, {
            objectId,
            left: target.left,
            top: target.top,
            angle: target.angle,
            trailerAngleOffset: 'trailerAngleOffset' in target
                ? (target as YjsTrackedFabricObject & { trailerAngleOffset: number }).trailerAngleOffset
                : undefined,
        });
    }

    /**
     * Clear the movingObject awareness state (call when drag/steer ends).
     * Always call syncObjectFull() BEFORE this to persist final position to Y.Doc.
     */
    clearMovingObjectAwareness(): void {
        this.publishEphemeral(MOVING_OBJECT_FIELD, null);
    }

    /**
     * Built-in `movingObject` handler: apply a remote object's dragged position.
     * Returns true if a canvas object was moved (so the listener re-renders).
     */
    private applyMovingObject(payload: unknown | null): boolean {
        if (!payload) return false;
        const { objectId, left, top, angle, trailerAngleOffset } =
            payload as { objectId: string; left: number; top: number; angle: number; trailerAngleOffset?: number };

        const canvasObj = this.findCanvasObjectById(objectId);
        if (!canvasObj) return false;

        canvasObj.set({ left, top, angle });
        if (trailerAngleOffset !== undefined && 'trailerAngleOffset' in canvasObj) {
            (canvasObj as YjsTrackedFabricObject & { trailerAngleOffset: number }).trailerAngleOffset = trailerAngleOffset;
        }
        canvasObj.setCoords();
        return true;
    }

    /**
     * Listen for remote awareness changes and dispatch each registered ephemeral
     * field to its handler. Skips own client (prevents echo). Departed clients
     * (`removed`) are dispatched as `null` so handlers can tear down transient state.
     */
    private setupAwarenessListener(): void {
        if (!this.awareness) return;

        this.awarenessChangeHandler = ({ added, updated, removed }) => {
            const awareness = this.awareness!;
            let needsRender = false;

            for (const clientId of [...added, ...updated]) {
                // Skip own client (prevent echo)
                if (clientId === awareness.clientID) continue;

                const state = awareness.getStates().get(clientId);
                for (const [field, apply] of this.ephemeralHandlers) {
                    if (apply(state?.[field] ?? null, clientId) === true) {
                        needsRender = true;
                    }
                }
            }

            // A departed client can no longer carry any field — let handlers tear
            // down anything they created for it (e.g. an in-progress preview stroke).
            for (const clientId of removed) {
                if (clientId === awareness.clientID) continue;
                for (const [, apply] of this.ephemeralHandlers) {
                    if (apply(null, clientId) === true) {
                        needsRender = true;
                    }
                }
            }

            if (needsRender) {
                this.canvas.renderAll();
            }
        };

        this.awareness.on('change', this.awarenessChangeHandler);
    }

    /**
     * Cleanup: Remove all listeners
     */
    destroy(): void {
        // Remove awareness listener and drop registered ephemeral handlers
        if (this.awarenessChangeHandler && this.awareness) {
            this.awareness.off('change', this.awarenessChangeHandler);
            this.awarenessChangeHandler = undefined;
        }
        this.ephemeralHandlers.clear();
        // Remove Fabric listeners (only set up for Teacher)
        if (this.handleObjectAdded) {
            this.canvas.off('object:added', this.handleObjectAdded);
        }
        if (this.handleObjectModified) {
            this.canvas.off('object:modified', this.handleObjectModified);
        }
        if (this.handleObjectRemoved) {
            this.canvas.off('object:removed', this.handleObjectRemoved);
        }

        // Remove Yjs deep observer from objects array
        this.detachYjsObserver();

        // Remove Yjs metadata observer (from the instance we actually observed)
        if (this.yMetadataObserver && this.observedMetadata) {
            this.observedMetadata.unobserve(this.yMetadataObserver);
            this.observedMetadata = undefined;
        }

        // Clear pending state maps to prevent memory leaks
        this.pendingUpdates.clear();
        this.pendingObjects.clear();
    }
}
